import type {ProjectId} from '@/types';

// ============================================================
// 通用
// ============================================================

/** 上传业务场景 */
export type UploadSource =
    | 'SCRIPT'
    | 'WORKBENCH'
    | 'CANVAS_BASE'
    | 'CANVAS_IMAGEGENERATOR'
    | 'CANVAS_VIDEOGENERATOR'
    | 'CANVAS_LIPSYNC'
    | 'STORYBOARD'
    | 'EDITOR'
    | 'SCRIPT_AGENT'
    | 'STORYBOARD_AGENT'
    | 'CANVAS_AGENT';

/** 文件类型分组，决定默认校验配置，图片、视频、音频、剧本 */
export type UploadFileType = 'IMAGE' | 'VIDEO' | 'AUDIO' | 'TXT';

/** 文件类型入参，AUTO 表示由文件 MIME/后缀自动推断 */
export type UploadFileTypeOption = UploadFileType | 'AUTO';

/** 上传方式 直传 分片 自动(默认) */
export type UploadTransport = 'direct' | 'chunk' | 'auto';

/** 文件大小限制分组 */
export type UploadSizeCategory = 'image' | 'video' | 'audio' | 'txt';

/** 文件最大字节数配置：统一限制或按文件分组限制 */
export type MaxSizeByte = number | Partial<Record<UploadSizeCategory, number>>;

/** 文件最大数量配置：统一限制或 AUTO 场景下按文件分组限制 */
export type MaxCount = number | (Partial<Record<UploadSizeCategory, number>> & {total?: number});

/** 风控状态 */
export type RiskStatus = 'pass' | 'blocked' | 'processing';

// ============================================================
// 上传配置
// ============================================================

/** 上传可选配置（业务方可覆盖默认值） */
export interface UploadOptions {
    /** 文件最大字节数，支持统一限制或按文件分组限制 */
    maxSizeByte?: MaxSizeByte;
    /** 允许的文件后缀（不含点号，如 ['jpg','png']） */
    allowedExtensions?: string[];
    /** 最大文件数，AUTO 场景支持按文件分组限制 */
    maxCount?: MaxCount;
    /** 是否多选，默认为否 */
    multiple?: boolean;
    /** 文件级并发数，一期暂不做 */
    concurrency?: number;
    /** 覆盖上传目标平台地址 */
    customUrl?: string;
    /** 上传方式：direct 直传 | chunk 分片 | auto 自动判断 */
    transport?: UploadTransport;
    /** auto 模式下分片阈值（字节），不得低于 10MB */
    chunkThreshold?: number;
    /** 单片大小（字节） */
    chunkSize?: number;
    /** 分片级并发数，视频流默认为 3 */
    chunkConcurrency?: number;
    /** 单片最大重试次数，默认为 2 */
    maxChunkRetry?: number;
}

// ============================================================
// 上传 Hook 出入参
// ============================================================

/** useUploadFile 入参 */
export interface UseUploadFileOptions {
    /** 上传的业务场景 */
    scene: UploadSource;
    /** 文件类型，决定默认配置组 */
    type: UploadFileTypeOption;
    /** 覆盖默认配置 */
    options?: UploadOptions;
    /** 上传流程错误回调，包含文件校验、上传、分片、风控等阶段 */
    onError?: (payload: {file?: File; error: UploadError}) => void;
    /** 额外请求头 反作弊预留 */
    additionalHeader?: Record<string, string>;
    /** 当前项目 ID；无项目上传场景不传 */
    projectId?: ProjectId;
}

/** 上传状态 */
export type UploadStatus = 'INIT' | 'READY' | 'UPLOADING' | 'SUCCESS' | 'ERROR';

/** 单文件上传对象 */
export interface UploadFileItem {
    /** 文件唯一标识 */
    id: string;
    /** 原始 File 对象 */
    file: File;
    /** 上传进度 0~100（分片有中间值，直传只有 0/100） */
    progress: number;
    /** 当前状态 */
    status: UploadStatus;
    /** 上传失败时的错误信息 */
    error?: UploadError;
    /** 上传成功后的服务端响应 */
    response?: UploadResult;
    /** 风控状态 */
    riskStatus?: RiskStatus;
    /** 视频异步风控任务ID */
    taskId?: string;
    /** 视频封面URL */
    coverUrl?: string;
}

/** submit 返回值 */
export interface SubmitResult {
    succeeded: UploadFileItem[];
    failed: UploadFileItem[];
}

/** useUploadFile 返回值 */
export interface UseUploadFileResult {
    /** 当前文件列表 */
    fileList: UploadFileItem[];
    /** 当前上传状态 */
    status: UploadStatus;
    /** 打开文件选择弹窗 */
    select: () => void;
    /** 发起上传 */
    submit: () => Promise<SubmitResult>;
    /** 重试单个失败文件 */
    retry: (id: string) => Promise<UploadFileItem>;
    /** 清空文件列表 */
    clear: () => void;
    /** 取消上传 */
    abort: () => void;
}

// ============================================================
// 上传 API 响应
// ============================================================

/** 直传响应 data */
export interface DirectUploadData {
    fileName: string;
    fileType: string;
    fileSize: number;
    fileUrl: string;
    imageWidth?: number;
    imageHeight?: number;
}

/** 统一上传响应（直传 & 合并共用，支持风控） */
export interface UploadResult {
    fileName?: string;
    fileType?: string;
    fileSize?: number; // 分片视频为空，server无法获取元数据
    fileUrl?: string; // 非视频风控通过时返回
    coverUrl?: string; // 视频封面URL
    taskId?: string; // 视频异步风控任务ID
    riskStatus: RiskStatus;
    reason?: string; // 风控拒绝原因
    imageWidth?: number; // 图片宽度
    imageHeight?: number; // 图片高度
}

/** 风控轮询响应 */
export interface RiskStatusResult {
    riskStatus: RiskStatus;
    fileUrl?: string; // 风控通过后的视频、音频URL
    reason?: string;
}

/** 分片初始化响应 data */
export interface ChunkInitData {
    uploadId: string;
    key: string;
}

/** 分片上传响应 data */
export interface ChunkUploadData {
    etag: string;
}

/** HostApi.upload 单 callback 状态联合（复用 UploadStatus 状态枚举） */
export type UploadState =
    | {
          status: Extract<UploadStatus, 'UPLOADING'>;
          completed: number;
          total: number;
          percent: number;
      }
    | {status: Extract<UploadStatus, 'UPLOADING'>; riskStatus: RiskStatus; response: UploadResult}
    | {status: Extract<UploadStatus, 'SUCCESS'>; result: UploadResult}
    | {status: Extract<UploadStatus, 'ERROR'>; error: UploadError};

// ============================================================
// 上传错误
// ============================================================

/** 错误发生阶段 */
export type UploadErrorStage =
    | 'VALIDATE'
    | 'UPLOAD'
    | 'CHUNK_INIT'
    | 'CHUNK_UPLOAD'
    | 'CHUNK_MERGE'
    | 'RISK_CHECK';

/** 错误码 */
export type UploadErrorCode =
    | 'FILE_COUNT_EXCEEDED'
    | 'FILE_TYPE_INVALID'
    | 'FILE_SIZE_EXCEEDED'
    | 'FILE_EMPTY'
    | 'UPLOAD_ABORTED'
    | 'UPLOAD_REQUEST_FAILED'
    | 'UPLOAD_RESPONSE_INVALID'
    | 'UPLOAD_SERVER_ERROR'
    | 'UPLOAD_INIT_FAILED'
    | 'CHUNK_UPLOAD_FAILED'
    | 'CHUNK_MERGE_FAILED'
    | 'RISK_BLOCKED'
    | 'RISK_TIMEOUT';

/** 错误码 → 默认提示信息 */
export const UPLOAD_ERROR_MESSAGES: Record<UploadErrorCode, string> = {
    FILE_COUNT_EXCEEDED: '超出最大文件数量限制',
    FILE_TYPE_INVALID: '文件类型不支持',
    FILE_SIZE_EXCEEDED: '文件大小超出限制，请重新上传',
    FILE_EMPTY: '文件内容为空',
    UPLOAD_ABORTED: '上传已取消',
    UPLOAD_REQUEST_FAILED: '上传请求失败，请稍后重试',
    UPLOAD_RESPONSE_INVALID: '上传响应异常，请稍后重试',
    UPLOAD_SERVER_ERROR: '上传失败，请稍后重试',
    UPLOAD_INIT_FAILED: '上传初始化失败，请稍后重试',
    CHUNK_UPLOAD_FAILED: '分片上传失败，请重试',
    CHUNK_MERGE_FAILED: '分片合并失败，请稍后重试',
    RISK_BLOCKED: '内容审核未通过',
    RISK_TIMEOUT: '内容审核超时，请稍后重试',
};

/** 上传错误对象 */
export class UploadError extends Error {
    /** 失败阶段 */
    stage: UploadErrorStage;
    /** 错误码 */
    status: UploadErrorCode;
    /** 原始异常/响应 */
    raw?: unknown;

    constructor(stage: UploadErrorStage, status: UploadErrorCode, raw?: unknown) {
        super(UPLOAD_ERROR_MESSAGES[status]);
        this.name = 'UploadError';
        this.stage = stage;
        this.status = status;
        this.raw = raw;
    }
}

// ============================================================
// 下载
// ============================================================

/** download() 入参 */
export interface DownloadOptions {
    /** 文件下载地址 */
    url: string;
    /** 保存文件名，不传则从 URL 解析 */
    fileName?: string;
    /** 是否追加 BOS responseContentDisposition 参数，不添加的话，跨域文件浏览器会忽略a标签的download属性，文件会新打开一个标签页而不是下载，本期所有资源均为BOS地址，默认追加 */
    forceAttachment?: boolean;
}

/** useFileDownload 返回值 */
export interface UseFileDownloadResult {
    /** 触发单文件下载 */
    download: (options: DownloadOptions) => void;
    /** 触发批量下载（非本期，已预留） */
    downloadAll: (optionsList: DownloadOptions[]) => void;
}
